// Exposure — Photoshop-style adjustment.
//
// Three sliders:
//   Exposure (-20..+20 EV, default 0)
//   Offset   (-0.5..+0.5, default 0)
//   Gamma    (0.01..9.99, default 1)
//
// Algorithm (per-channel):
//   v = channel / 255
//   v = v * 2^exposure
//   v = v + offset
//   v = v^(1/gamma)
//   out = clamp01(v) * 255

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface ExposureParams {
  /** Exposure in EV, -20..+20. Default 0. */
  exposure: number;
  /** Offset, -0.5..+0.5. Default 0. */
  offset: number;
  /** Gamma correction, 0.01..9.99. Default 1. */
  gamma: number;
}

const DEFAULT_PARAMS: ExposureParams = {
  exposure: 0,
  offset: 0,
  gamma: 1,
};

export const Exposure: Filter<ExposureParams> = {
  id: "exposure",
  displayName: "Exposure",
  defaultParams: DEFAULT_PARAMS,

  apply(
    input: PixelBuffer,
    params: ExposureParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    const exposure = clampRange(params.exposure, -20, 20);
    const offset = clampRange(params.offset, -0.5, 0.5);
    const gamma = clampRange(params.gamma, 0.01, 9.99);

    const exposureScale = Math.pow(2, exposure);
    const invGamma = 1 / gamma;

    for (let i = 0; i < n; i++) {
      const o = i * 4;
      const r = src[o] ?? 0;
      const g = src[o + 1] ?? 0;
      const b = src[o + 2] ?? 0;
      const a = src[o + 3] ?? 0;

      if (a === 0) {
        dst[o] = 0;
        dst[o + 1] = 0;
        dst[o + 2] = 0;
        dst[o + 3] = 0;
        continue;
      }

      const vr = (r / 255) * exposureScale + offset;
      const vg = (g / 255) * exposureScale + offset;
      const vb = (b / 255) * exposureScale + offset;

      dst[o] = Math.round(clamp01(Math.pow(vr, invGamma)) * 255);
      dst[o + 1] = Math.round(clamp01(Math.pow(vg, invGamma)) * 255);
      dst[o + 2] = Math.round(clamp01(Math.pow(vb, invGamma)) * 255);
      dst[o + 3] = a;
    }

    return out;
  },
};

function clampRange(v: number, lo: number, hi: number): number {
  if (!isFinite(v)) return 0;
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
